'use client';

import { Placeholder, Text } from '@lmn/design-system';
import { css } from 'styled-components';
import { lastMinuteDealsGradient } from '@/app/common/components/Promotions/styled';
import { useHotelPromotion } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { useStickyHeaderOffset } from '@/app/components/hotel-detail/hooks/useStickyHeaderOffset.ts';
import { useMeasure } from 'react-use';
import { useTranslation } from '../translations/Translation';
import { useOffersContext } from './OffersProvider';
import customCss from './css/customCss';
import { RoomPanelPrice } from './rooms/RoomPanelPrice';

export function PriceSticky(props: {
  fixedOnTop?: boolean;
  // This is the margin bottom of the package details section.
  // The price sticky is applying a negative margin top to place itself above the package details section,
  // specifically over the price. To be more precise, it is needed to know the margin bottom of the package
  // details section to calculate the correct position of the sticky price and avoid jumps.
  marginBottom?: number;
}) {
  const { fixedOnTop = false, marginBottom = 0 } = props;
  const [ref, { height }] = useMeasure();
  const { offersData, isLoadingRooms } = useOffersContext();
  const { t } = useTranslation();
  const { stickyTop, stickyTopCss } = useStickyHeaderOffset();
  const { showLmnDealsPromotion, showFlashSalesPromotion } = useHotelPromotion();
  const showPromotionLayout = showLmnDealsPromotion ? offersData?.hasFlashSaleOffers : showFlashSalesPromotion;
  const cheapestFilteredMealPlan = isLoadingRooms ? undefined : offersData?.cheapestFilteredMealPlan;

  if (isLoadingRooms) return null;

  const handleClick = () => {
    const packageDetailsElement = document.getElementById('packageDetails');
    if (packageDetailsElement) {
      // stickyTop keeps the target clear of the fixed header band this component pins below.
      window.scrollTo({ top: packageDetailsElement.offsetTop - stickyTop - 16, behavior: 'smooth' });
    }
  };

  const cssHeight = height + marginBottom;

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      role="button"
      tabIndex={0}
      aria-label={t('vacation.select.summary.sticky.cta')}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      css={`
        position: sticky;
        ${stickyTopCss};
        z-index: ${fixedOnTop ? 1 : 0};
        visibility: ${fixedOnTop ? 'visible' : 'hidden'};

        margin: -${cssHeight}px 0 ${marginBottom}px 0;
        padding: 0;

        cursor: pointer;
        box-shadow: 0px 4px 8px 0px #0000000a;
        background: ${
          showPromotionLayout && !fixedOnTop
            ? showLmnDealsPromotion
              ? lastMinuteDealsGradient
              : customCss.gradients.flashSaleDeal
            : 'transparent'
        };

        @media ${customCss.breakpoints.sm} {
          padding: 0;
          margin: -${cssHeight}px 24px ${marginBottom}px 24px;
          border-radius: ${fixedOnTop ? '0 0 8px 8px' : '0'};
          background: white;
        }

        @media ${customCss.breakpoints.lg} {
          background: ${fixedOnTop ? 'white' : 'transparent'};
          box-shadow: ${fixedOnTop ? '0px 4px 8px 0px #0000000a' : 'unset'};
          padding: ${fixedOnTop ? '0' : '0'};
        }

        @media ${customCss.breakpoints.lgPlus} {
          margin: -${cssHeight}px 40px ${marginBottom}px;
        }

        @media ${customCss.breakpoints.xl} {
          margin: -${cssHeight}px 0 ${marginBottom}px 0;
        }
      `}
    >
      <div
        css={`
          padding: ${fixedOnTop ? '8px 16px' : '8px 16px 16px 16px'};
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 0 0 8px 8px;
          background: white;

          @media ${customCss.breakpoints.lg} {
            padding: 8px 24px;
            background: transparent;
          }
        `}
      >
        <div
          css={`
            background: white;
            display: flex;
            flex-direction: column;
            opacity: ${fixedOnTop ? 1 : 0};
            transition: all 0.3s ease-in-out;
          `}
        >
          <Text variant="Title5">{t('vacation.select.summary.sticky.title')}</Text>
          <Text
            variant="BodySmall"
            css={css`
              color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
            `}
          >
            {t('vacation.select.summary.sticky.cta')}
          </Text>
        </div>
        {isLoadingRooms ? (
          <Placeholder height="24px" width="100px" />
        ) : offersData?.rooms ? (
          <RoomPanelPrice
            mealPlan={cheapestFilteredMealPlan}
            roomsIncludedInPrice={offersData.rooms[0].roomsIncludedInPrice}
            roomId={offersData.rooms[0].roomId}
            hidePromotion={true}
            hideLmnDeal={true}
            css={css`
              justify-content: flex-end;
              align-items: flex-end;
              text-align: right;
            `}
          />
        ) : null}
      </div>
    </div>
  );
}
